import { LimeModal } from "@/Components/NextBase/LimeModal";
import { useConsent } from "@/context/ConsentContext";
import type { ConsentCategories } from "@/utils/consent";
import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { useState } from "react";

export function ConsentPreferences() {
  const { preferencesOpen, closePreferences, analytics, marketing, save } =
    useConsent();

  return (
    <LimeModal
      isOpen={preferencesOpen}
      onClose={closePreferences}
      data-identifier="consent-preferences"
    >
      <ModalContent>
        {() => (
          <ConsentPreferencesForm
            initialAnalytics={analytics}
            initialMarketing={marketing}
            onSave={save}
          />
        )}
      </ModalContent>
    </LimeModal>
  );
}

// Mounted fresh each time the modal opens (HeroUI unmounts closed content), so
// the toggles seed from the current decision without a setState-in-effect. The
// category labels + descriptions mirror the Complianz CMP on the marketing site
// so a decision imported from there matches what our own banner presents.
function ConsentPreferencesForm({
  initialAnalytics,
  initialMarketing,
  onSave,
}: {
  initialAnalytics: boolean;
  initialMarketing: boolean;
  onSave: (categories: ConsentCategories) => void;
}) {
  const [analytics, setAnalytics] = useState(initialAnalytics);
  const [marketing, setMarketing] = useState(initialMarketing);
  const { statement } = useConsent();

  return (
    <>
      <ModalHeader>
        <Trans>Nastavitve piškotkov</Trans>
      </ModalHeader>
      <ModalBody>
        <p className="text-foreground-600 text-sm">{statement}</p>

        <div className="flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <span className="font-medium text-foreground text-sm">
                <Trans>Funkcijski</Trans>
              </span>
              <p className="text-foreground-500 text-xs">
                <Trans>
                  Tehnična hramba oziroma dostop je nujno potreben za zakonit
                  namen omogočanja uporabe določene storitve, ki jo naročnik ali
                  uporabnik izrecno zahteva, ali izključno za namen izvajanja
                  prenosa komunikacije po elektronskem komunikacijskem omrežju.
                </Trans>
              </p>
            </div>
            <span className="shrink-0 whitespace-nowrap font-medium text-primary text-xs">
              <Trans>Vedno aktiven</Trans>
            </span>
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <span className="font-medium text-foreground text-sm">
                <Trans>Statistični</Trans>
              </span>
              <p className="text-foreground-500 text-xs">
                <Trans>
                  Tehnično shranjevanje ali dostop, ki se uporablja izključno za
                  statistične namene.
                </Trans>
              </p>
            </div>
            <Switch
              isSelected={analytics}
              onValueChange={setAnalytics}
              aria-label="Statistični"
              className="shrink-0"
              data-identifier="consent-analytics"
            />
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <span className="font-medium text-foreground text-sm">
                <Trans>Oglaševalski</Trans>
              </span>
              <p className="text-foreground-500 text-xs">
                <Trans>
                  Tehnična hramba ali dostop je potreben za ustvarjanje
                  uporabniških profilov za pošiljanje oglasov ali za sledenje
                  uporabniku na spletnem mestu ali več spletnih mestih za
                  podobne tržne namene.
                </Trans>
              </p>
            </div>
            <Switch
              isSelected={marketing}
              onValueChange={setMarketing}
              aria-label="Oglaševalski"
              className="shrink-0"
              data-identifier="consent-marketing"
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="bordered"
          onPress={() => onSave({ analytics: false, marketing: false })}
          data-identifier="consent-reject"
        >
          <Trans>Zavrni</Trans>
        </Button>
        <Button
          variant="bordered"
          onPress={() => onSave({ analytics, marketing })}
          data-identifier="consent-save"
        >
          <Trans>Shrani nastavitve</Trans>
        </Button>
        <Button
          color="primary"
          onPress={() => onSave({ analytics: true, marketing: true })}
          data-identifier="consent-accept"
        >
          <Trans>Sprejmi</Trans>
        </Button>
      </ModalFooter>
    </>
  );
}
